import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";

import { connect } from "react-redux";

import { ZoomMtg } from "@zoom/meetingsdk";

import CryptoJS from "crypto-js";

import "./zoom_component.scss";


const Zoom = ({
  userData,
  liveStreamData,
}) => {

  const [zoomState, setZoomState] =
    useState({
      isInitialized: false,
      isJoining: false,
      error: null,
    });


  /*
   * ============================================================
   * SERVER TIME OFFSET
   * ============================================================
   *
   * Example:
   *
   * Browser time = 10:20
   * Server time  = 10:05
   *
   * offset = -15 minutes
   *
   * We then calculate:
   *
   * correctedTime = Date.now() + offset
   *
   * So the user's incorrect system clock does not directly
   * decide the Zoom JWT timestamp.
   */

  const serverTimeOffsetRef = useRef(0);


  /*
   * Prevent duplicate join attempts
   */

  const hasJoinedRef = useRef(false);


  // ============================================================
  // Stop Microsoft Clarity when Zoom loads
  // ============================================================

  useEffect(() => {

    if (window.clarity) {
      window.clarity("stop");
    }

  }, []);


  // ============================================================
  // ERROR HANDLER
  // ============================================================

  const setError = useCallback(
    (message) => {

      setZoomState((prev) => ({
        ...prev,
        isJoining: false,
        error: message,
      }));

    },
    []
  );


  // ============================================================
  // DECRYPT
  // ============================================================

  const decrypt = useCallback(
    (value) => {

      try {

        if (!value) {
          return null;
        }


        const encrypted =
          JSON.parse(atob(value));


        const key =
          CryptoJS.enc.Base64.parse(
            "0CiuKpLCZbX7gBCyYTka4USxTnh7ssXKk8bJ6Y3TXPc="
          );


        const decrypted =
          CryptoJS.AES.decrypt(
            encrypted.value,
            key,
            {
              iv:
                CryptoJS.enc.Base64.parse(
                  encrypted.iv
                ),
            }
          );


        return decrypted
          .toString(CryptoJS.enc.Utf8)
          .split("*")[1];


      } catch (error) {

        console.error(
          "Zoom decrypt error:",
          error
        );

        return null;
      }

    },
    []
  );


  // ============================================================
  // SYNC WITH SERVER TIME
  // ============================================================
  //
  // We use the HTTP Date header returned by our server.
  //
  // IMPORTANT:
  //
  // No backend code change is required IF your server already
  // returns the standard HTTP "Date" response header.
  //
  // ============================================================

  const syncServerTime = useCallback(
    async () => {

      try {

        /*
         * Record browser time before request
         */

        const requestStart =
          Date.now();


        /*
         * HEAD request avoids downloading the
         * complete page.
         */

        const response =
          await fetch(
            window.location.href,
            {
              method: "HEAD",

              cache: "no-store",

              credentials: "include",
            }
          );


        /*
         * Record browser time after request
         */

        const requestEnd =
          Date.now();


        /*
         * Standard HTTP server Date header
         */

        const serverDate =
          response.headers.get("date");


        if (!serverDate) {

          console.warn(
            "Zoom: Server Date header not available."
          );

          /*
           * Keep offset 0 as fallback.
           */

          serverTimeOffsetRef.current = 0;

          return Date.now();
        }


        const serverTimestamp =
          new Date(
            serverDate
          ).getTime();


        /*
         * Calculate approximate midpoint of request.
         *
         * This reduces the effect of network latency.
         */

        const browserRequestMidpoint =
          requestStart +
          (requestEnd - requestStart) / 2;


        /*
         * Server time - browser time
         */

        const offset =
          serverTimestamp -
          browserRequestMidpoint;


        serverTimeOffsetRef.current =
          offset;


        /*
         * Corrected current server time
         */

        const correctedServerTime =
          Date.now() + offset;


        console.info(
          "Zoom server time synchronization:",
          {
            browserTime:
              new Date().toISOString(),

            serverTime:
              new Date(
                correctedServerTime
              ).toISOString(),

            offsetMs:
              Math.round(offset),

            offsetSeconds:
              Math.round(
                offset / 1000
              ),
          }
        );


        return correctedServerTime;


      } catch (error) {

        console.warn(
          "Zoom server time synchronization failed:",
          error
        );


        /*
         * Fallback if server time cannot be retrieved.
         */

        serverTimeOffsetRef.current = 0;

        return Date.now();
      }

    },
    []
  );


  // ============================================================
  // GET CURRENT SERVER TIME
  // ============================================================

  const getServerTime = useCallback(
    () => {

      /*
       * Current browser time + previously calculated
       * server offset.
       */

      return (
        Date.now() +
        serverTimeOffsetRef.current
      );

    },
    []
  );


  // ============================================================
  // BASE64 URL ENCODER
  // ============================================================
  //
  // Your old code used btoa() directly.
  //
  // JWT uses Base64URL encoding.
  //
  // ============================================================

  const base64UrlEncode =
    useCallback(
      (value) => {

        return btoa(
          JSON.stringify(value)
        )
          .replace(/\+/g, "-")
          .replace(/\//g, "_")
          .replace(/=+$/, "");

      },
      []
    );


  // ============================================================
  // GENERATE ZOOM SIGNATURE
  // ============================================================

  const generateSignature = useCallback(
    (
      meetingNumber,
      role,
      sdkKey,
      sdkSecret,
      serverTimestamp
    ) => {

      try {

        /*
         * ======================================================
         * IMPORTANT CHANGE
         * ======================================================
         *
         * DO NOT use:
         *
         * Date.now()
         *
         * directly here.
         *
         * serverTimestamp comes from our server-time
         * synchronization.
         */

        const now =
          Math.floor(
            serverTimestamp / 1000
          );


        /*
         * Give Zoom 30 seconds tolerance.
         */

        const iat =
          now - 30;


        /*
         * Token valid for 2 hours.
         */

        const exp =
          iat + 60 * 60 * 2;


        const payload = {

          sdkKey,

          mn:
            String(meetingNumber),

          role,

          iat,

          exp,

          appKey:
            sdkKey,

          tokenExp:
            exp,
        };


        /*
         * JWT HEADER
         */

        const encodedHeader =
          base64UrlEncode({
            alg: "HS256",
            typ: "JWT",
          });


        /*
         * JWT PAYLOAD
         */

        const encodedPayload =
          base64UrlEncode(
            payload
          );


        /*
         * JWT unsigned part
         */

        const unsignedToken =
          `${encodedHeader}.${encodedPayload}`;


        /*
         * HMAC SHA256 signature
         */

        const signature =
          CryptoJS.HmacSHA256(
            unsignedToken,
            sdkSecret
          )
            .toString(
              CryptoJS.enc.Base64
            )
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, "");


        return (
          `${unsignedToken}.${signature}`
        );


      } catch (error) {

        console.error(
          "Zoom signature generation error:",
          error
        );

        return null;
      }

    },
    [base64UrlEncode]
  );


  // ============================================================
  // HANDLE JOIN MEETING
  // ============================================================

  const handleJoinMeeting =
    useCallback(
      async () => {

        /*
         * Prevent duplicate calls
         */

        if (
          !zoomState.isInitialized ||
          zoomState.isJoining ||
          hasJoinedRef.current
        ) {
          return;
        }


        /*
         * Check meeting data
         */

        if (!liveStreamData) {

          setError(
            "Meeting data not available"
          );

          return;
        }


        /*
         * Mark joining
         */

        setZoomState(
          (prev) => ({
            ...prev,

            isJoining: true,

            error: null,
          })
        );


        try {

          // ====================================================
          // SYNC SERVER TIME BEFORE GENERATING JWT
          // ====================================================

          const serverTime =
            await syncServerTime();


          /*
           * Convert server time to Unix timestamp
           */

          const serverUnixTime =
            Math.floor(
              serverTime / 1000
            );


          console.info(
            "Zoom server time:",
            {
              serverTime:
                new Date(
                  serverTime
                ).toISOString(),

              serverUnixTime,

              browserTime:
                new Date().toISOString(),

              offsetSeconds:
                Math.round(
                  serverTimeOffsetRef.current /
                  1000
                ),
            }
          );


          // ====================================================
          // DECRYPT EXISTING ZOOM DATA
          // ====================================================

          const meetingNumber =
            decrypt(
              liveStreamData.zoom_id
            );


          const password =
            decrypt(
              liveStreamData.password
            );


          const sdkKey =
            decrypt(
              liveStreamData.sdk_key
            );


          const sdkSecret =
            decrypt(
              liveStreamData.sdk_secret_key
            );


          const userEmail =
            decrypt(
              liveStreamData.zoom_email
            );


          // ====================================================
          // VALIDATION
          // ====================================================

          if (
            !meetingNumber ||
            !sdkKey ||
            !sdkSecret
          ) {

            console.error(
              "Invalid Zoom configuration:",
              {
                meetingNumber:
                  Boolean(meetingNumber),

                sdkKey:
                  Boolean(sdkKey),

                sdkSecret:
                  Boolean(sdkSecret),
              }
            );


            setError(
              "Invalid meeting configuration"
            );

            return;
          }


          // ====================================================
          // GENERATE SIGNATURE
          // ====================================================

          const signature =
            generateSignature(
              meetingNumber,

              0,

              sdkKey,

              sdkSecret,

              serverTime
            );


          if (!signature) {

            setError(
              "Unable to generate Zoom signature"
            );

            return;
          }


          // ====================================================
          // USER NAME
          // ====================================================

          const userName =
            `${userData?.roll_no || "User"}-${
              userData?.name || "Guest"
            }`;


          // ====================================================
          // SAFE DEBUG LOG
          // ====================================================
          //
          // DO NOT log:
          // - sdkSecret
          // - password
          // - signature
          //
          // ====================================================

          // ============================================================
          // DETAILED SAFE ZOOM DEBUG LOGS
          // ============================================================
          // DO NOT log:
          // - sdkSecret
          // - password
          // - full signature
          // ============================================================

          console.group("========== ZOOM JOIN DEBUG ==========");

          console.log("1. Meeting Number (last 4):",
            String(meetingNumber).slice(-4)
          );
          console.log("2. SDK Key:", sdkKey);
          console.log("3. User Name:", userName);
          console.log("4. User Email:", userEmail);

          console.log(
            "5. Server Time:",
            new Date(serverTime).toISOString()
          );
          console.log("6. Server Unix Time:", serverUnixTime);

          console.log(
            "7. Browser Time:",
            new Date().toISOString()
          );

          console.log(
            "8. Time Difference (seconds):",
            Math.round(
              serverTimeOffsetRef.current / 1000
            )
          );

          console.log("9. Browser:", navigator.userAgent);
          console.log(
            "10. Timezone:",
            Intl.DateTimeFormat()
              .resolvedOptions()
              .timeZone
          );
          console.log("11. Online:", navigator.onLine);
          console.log("12. Zoom SDK Version:", "3.13.2");

          console.log(
            "13. Signature Generated:",
            Boolean(signature)
          );
          console.log(
            "14. Signature Length:",
            signature?.length
          );
          console.log(
            "15. Signature Format Valid:",
            signature?.split(".").length === 3
          );

          // Decode JWT for debugging only.
          // This does NOT expose the signing secret.
          try {
            const [header, payload] = signature.split(".");

            const decodeBase64Url = (value) => {
              const base64 = value
                .replace(/-/g, "+")
                .replace(/_/g, "/");

              return JSON.parse(atob(base64));
            };

            const decodedHeader = decodeBase64Url(header);
            const decodedPayload = decodeBase64Url(payload);

            console.group(
              "========== ZOOM JWT DEBUG =========="
            );

            console.log("16. JWT Header:", decodedHeader);
            console.log("17. JWT Payload:", decodedPayload);

            console.log(
              "18. JWT iat:",
              decodedPayload.iat,
              new Date(
                decodedPayload.iat * 1000
              ).toISOString()
            );

            console.log(
              "19. JWT exp:",
              decodedPayload.exp,
              new Date(
                decodedPayload.exp * 1000
              ).toISOString()
            );

            console.log(
              "20. Current Server Unix:",
              serverUnixTime
            );

            console.log(
              "21. Token Age:",
              serverUnixTime - decodedPayload.iat,
              "seconds"
            );

            console.log(
              "22. Token Remaining:",
              decodedPayload.exp - serverUnixTime,
              "seconds"
            );

            console.groupEnd();
          } catch (jwtDebugError) {
            console.error(
              "JWT debug decode failed:",
              jwtDebugError
            );
          }

          console.groupEnd();


          // ====================================================
          // JOIN ZOOM
          // ====================================================

          console.info(
            "========== ZOOM JOIN REQUEST ==========",
            {
              meetingNumber: String(meetingNumber).slice(-4),
              sdkKey,
              userName,
              userEmail,
              hasPassword: Boolean(password),
              hasSignature: Boolean(signature),
              signatureLength: signature?.length,
            }
          );

          ZoomMtg.join({

            meetingNumber,

            signature,

            sdkKey,

            userName,

            userEmail,

            passWord:
              password,


            // ==================================================
            // SUCCESS
            // ==================================================

            success: (success) => {

              console.info(
                "Zoom join successful:",
                success
              );


              hasJoinedRef.current =
                true;


              setZoomState(
                (prev) => ({
                  ...prev,

                  isJoining: false,

                  error: null,
                })
              );

            },


            // ==================================================
            // ERROR
            // ==================================================

            error: (error) => {

              console.group(
                "========== ZOOM JOIN ERROR =========="
              );

              console.error("Raw Zoom Error:", error);
              console.error("Reason:", error?.reason);
              console.error("Message:", error?.message);
              console.error("Type:", error?.type);
              console.error("Code:", error?.code);
              console.error("Browser:", navigator.userAgent);
              console.error("Online:", navigator.onLine);

              console.groupEnd();


              /*
               * Keep actual Zoom error.
               */

              const zoomError =
                error?.reason ||
                error?.message ||
                error?.type ||
                "Failed to join meeting";


              hasJoinedRef.current =
                false;


              setError(
                `Zoom join failed: ${zoomError}`
              );

            },

          });


        } catch (error) {

          console.error(
            "Zoom join exception:",
            error
          );


          hasJoinedRef.current =
            false;


          setError(
            error?.message ||
            "Unexpected error"
          );

        }

      },
      [
        decrypt,
        generateSignature,
        liveStreamData,
        setError,
        syncServerTime,
        userData,
        zoomState.isInitialized,
        zoomState.isJoining,
      ]
    );


  // ============================================================
  // INITIALIZE ZOOM
  // ============================================================

  useEffect(() => {

    const initializeZoom =
      async () => {

        try {

          ZoomMtg.setZoomJSLib(
            "https://source.zoom.us/3.13.2/lib",
            "/av"
          );


          ZoomMtg.preLoadWasm();


          ZoomMtg.prepareWebSDK();


          await ZoomMtg.init({

            leaveUrl:
              "https://learning.motion.ac.in/dashboard",

            isSupportAV: true,


            success: () => {

              console.info(
                "Zoom initialization successful"
              );


              setZoomState(
                (prev) => ({
                  ...prev,

                  isInitialized: true,

                  error: null,
                })
              );

            },


            error: (error) => {

              console.error(
                "Zoom initialization error:",
                error
              );


              setError(
                error?.reason ||
                error?.message ||
                "Zoom initialization failed"
              );

            },

          });

        } catch (error) {

          console.error(
            "Zoom initialization exception:",
            error
          );


          setError(
            error?.message ||
            "Zoom initialization failed"
          );

        }

      };


    initializeZoom();

  }, [setError]);


  // ============================================================
  // AUTO JOIN
  // ============================================================

  useEffect(() => {

    if (
      zoomState.isInitialized &&
      liveStreamData
    ) {

      handleJoinMeeting();

    }

  }, [
    zoomState.isInitialized,
    liveStreamData,
    handleJoinMeeting,
  ]);


  // ============================================================
  // UI
  // ============================================================

  return (
    <>

      {zoomState.error && (

        <div
          className="error-toast"
          role="alert"
        >
          {zoomState.error}
        </div>

      )}


      <div
        id="meetingSDKElement"
        data-clarity-mask="true"
      >

        <div id="zmmtg-root" />

      </div>

    </>
  );
};


// ============================================================
// REDUX
// ============================================================

const mapStateToProps =
  (state) => ({

    liveStreamData:
      state.userActivity.liveStreamData,

    userData:
      state.auth.user,

  });


export default connect(
  mapStateToProps
)(Zoom);